/**
 * Install Follow-Up Engine
 * 
 * ⚠️  INTERNAL MODULE — Do not import directly from application code.
 *     Use `unifiedFollowUp.ts` as the single entry point.
 * 
 * MODULE OWNERSHIP: Follow-up domain — install context (see moduleOwnership.ts)
 * RESPONSIBILITY: Convert incomplete installer wrap outcomes into trackable
 * follow-up tasks for dispatch/scheduling.
 * 
 * ENFORCEMENT: This module is guarded by assertInternalAccess().
 * Unauthorized imports will throw in DEV and warn in PROD.
 */

/** @internal — Marker for architecture enforcement. Do not remove. */
export const INTERNAL_ONLY = true as const;
export const INTERNAL_FACADE = "unifiedFollowUp.ts" as const;

import type { InstallerWrapRecord, InstallOutcome } from "./installerWrapEngine";

// ── Install Follow-Up Task ──

export type InstallFollowUpStatus = "open" | "scheduled" | "completed" | "cancelled";

export type InstallFollowUpReason =
  | "partial_reschedule"
  | "blocked_issue"
  | "customer_delay";

export interface InstallFollowUpTask {
  id: string;
  jobId: string;
  wrapRecordId: string;
  type: "install_follow_up";
  reason: InstallFollowUpReason;
  reasonLabel: string;
  communicationSummary: string;
  proposedNextDate?: string;
  completionPercent: number;
  assignedTo: "dispatch";
  status: InstallFollowUpStatus;
  createdAt: string;
  resolvedAt?: string;
  resolvedBy?: string;
}

// ── Reason Labels ──

const REASON_LABELS: Record<InstallFollowUpReason, string> = {
  partial_reschedule: "Partial Install — Reschedule Required",
  blocked_issue: "Blocked by Issue — Resolution Required",
  customer_delay: "Customer Delay / Deferral — Outreach Required",
};

// ── Trigger Check ──

const TRIGGERING_OUTCOMES: InstallOutcome[] = [
  "partial_reschedule",
  "blocked_issue",
  "customer_delay",
];

export function shouldCreateFollowUp(outcome: InstallOutcome): boolean {
  return TRIGGERING_OUTCOMES.includes(outcome);
}

// ── Duplicate Protection ──

export function hasExistingFollowUp(
  jobId: string,
  wrapDate: string,
  existingTasks: InstallFollowUpTask[],
): boolean {
  const dateStr = wrapDate.slice(0, 10);
  return existingTasks.some(
    t => t.jobId === jobId
      && t.createdAt.slice(0, 10) === dateStr
      && t.status === "open",
  );
}

// ── Task Creation ──

export function createInstallFollowUp(
  wrap: InstallerWrapRecord,
  existingTasks: InstallFollowUpTask[],
): InstallFollowUpTask | null {
  if (!shouldCreateFollowUp(wrap.outcome)) return null;
  if (hasExistingFollowUp(wrap.jobId, wrap.timestamp, existingTasks)) return null;

  const reason = wrap.outcome as InstallFollowUpReason;

  return {
    id: `IFU-${Date.now().toString(36).toUpperCase()}`,
    jobId: wrap.jobId,
    wrapRecordId: wrap.id,
    type: "install_follow_up",
    reason,
    reasonLabel: REASON_LABELS[reason],
    communicationSummary: wrap.customerComm.communicationSummary,
    proposedNextDate: wrap.customerComm.proposedNextDate,
    completionPercent: wrap.completionPercent,
    assignedTo: "dispatch",
    status: "open",
    createdAt: new Date().toISOString(),
  };
}
